import { useState } from 'react';
import {
  Callout,
  Confirm,
  Empty,
  Facts,
  Loading,
  Modal,
  Panel,
  TableScroll,
  formatDay,
  rowOpen,
  type Load,
} from '@affdice/portal-kit';
import { PlatformApi } from '../../../client.ts';
import { t } from '../../../messages.ts';
import { platformInvoiceFacts, platformInvoiceGross } from '../../../screens.ts';

/** Platform invoices — what the platform has billed this operator, and recording one paid. */
export function PlatformInvoicesSection({
  api,
  operatorId,
  canWrite,
  invoices,
  refresh,
}: {
  api: PlatformApi;
  operatorId: string;
  canWrite: boolean;
  invoices: Load<Awaited<ReturnType<PlatformApi['platformInvoices']>>>;
  refresh: () => void;
}) {
  // Which platform invoice's own popup is open, by id rather than by value:
  // the row is looked up again on each render, so marking one paid leaves the
  // popup showing the state the list now holds rather than a copy taken when
  // it was opened.
  const [openInvoice, setOpenInvoice] = useState<string | null>(null);
  // Batch 2 item 5 — which invoice's "Mark paid" is being confirmed, by id
  // for the same reason `openInvoice` is: the row is looked up again on
  // each render rather than a copy taken when the button was pressed.
  const [markingPaid, setMarkingPaid] = useState<string | null>(null);
  const openInvoiceRow = invoices.state === 'ready'
    ? invoices.value.items.find((i) => i.id === openInvoice)
    : undefined;
  // Batch 2 item 5 — which invoice's "Mark paid" is being confirmed.
  const markingPaidRow = invoices.state === 'ready'
    ? invoices.value.items.find((i) => i.id === markingPaid)
    : undefined;
  return (
    <Panel title={t('ops.invoices.title')}>
      {invoices.state !== 'ready' ? (
        <Loading rows={1} />
      ) : invoices.value.items.length === 0 ? (
        <Empty>{t('ops.invoices.none')}</Empty>
      ) : (
        <TableScroll>
          <table>
            <thead>
              <tr>
                <th>{t('ops.invoices.number')}</th><th>{t('common.period')}</th><th className="num">{t('ops.invoices.gross')}</th>
                <th>{t('common.state')}</th><th className="col-action" />
              </tr>
            </thead>
            <tbody>
              {invoices.value.items.map((inv) => {
                const openThis = () => setOpenInvoice(inv.id);
                return (
                /* The row opens the invoice; the button inside it records a
                   payment. Both are worth having and only one of them can
                   be what a press meant, so the button stops the press
                   before it reaches the row. */
                <tr key={inv.id} className="row-clickable" onClick={openThis} {...rowOpen(openThis)}>
                  <td>{inv.number}</td>
                  {/* §22.1 — same bare dates as the usage table above. */}
                  <td>{t('ops.invoices.range', { from: formatDay(inv.period_start), to: formatDay(inv.period_end) })}</td>
                  <td className="num money">
                    {platformInvoiceGross(inv)}
                  </td>
                  <td>{inv.state}</td>
                  {/* Pinned to the right edge: this table is wider than the
                      popup that holds it, and "Mark paid" was the column
                      that had scrolled off it. */}
                  <td className="num col-action">
                    {canWrite && inv.state === 'ISSUED' ? (
                      <button
                        className="copy"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Batch 2 item 5 — one click used to record the
                          // payment straight away; it now confirms first,
                          // with the same payload as before.
                          setMarkingPaid(inv.id);
                        }}
                      >
                        {t('ops.invoices.markPaid')}
                      </button>
                    ) : null}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </TableScroll>
      )}
      {invoices.state === 'ready' ? <Callout>{invoices.value.note}</Callout> : null}

      {markingPaidRow ? (
        <Confirm
          title={t('ops.invoices.markTitle', { number: markingPaidRow.number })}
          confirmLabel={t('ops.invoices.markPaid')}
          body={
            // Batch 2 review, finding 2 — markPlatformInvoicePaid does two
            // things this dialog used to deny: it sets the invoice itself
            // to MARKED_PAID (not just a separate settlement mark beside
            // it), and — when no other invoice issued over 60 days ago is
            // still unpaid afterwards — it lifts the operator's platform
            // restriction.
            <>
              {t('ops.invoices.markBefore')}{' '}
              <span className="mono">staff-{markingPaidRow.number}</span>
              {t('ops.invoices.markAfter')}
            </>
          }
          onCancel={() => setMarkingPaid(null)}
          onConfirm={async () => {
            await api.markPlatformInvoicePaid(operatorId, markingPaidRow.id, {
              reference: `staff-${markingPaidRow.number}`,
              paid_on: new Date().toISOString().slice(0, 10),
            });
            setMarkingPaid(null);
            refresh();
          }}
        />
      ) : null}

      {/*
        What the row already carries, laid out to be read rather than
        scanned. There is no per-invoice read on the platform side — the
        list route is the only one that returns a platform invoice, and the
        only other thing the API offers for one is the settlement mark that
        records it paid — so this popup shows the row's own fields and does
        not imply a document it cannot open.
      */}
      {openInvoiceRow ? (
        <Modal title={t('ops.invoices.invoiceTitle', { number: openInvoiceRow.number })} onClose={() => setOpenInvoice(null)}>
          <Facts items={platformInvoiceFacts(openInvoiceRow)} />
          <Callout>
            {t('ops.invoices.invoiceNote')}
          </Callout>
        </Modal>
      ) : null}
    </Panel>
  );
}
